import { useEffect, useRef, useState } from "react";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { adminApi } from "@/lib/adminApi";

interface ImageUploaderProps {
  /** Public URL of an already-uploaded image, or empty string. */
  value: string;
  /** Called with the uploaded URL on success, or "" to clear. */
  onChange: (url: string) => void;
  label?: string;
  maxSizeMB?: number;
  accept?: string;
  disabled?: boolean;
}

type Status = "idle" | "selected" | "uploading" | "error";

function formatErrorMessage(err: unknown, fallback = "Upload failed"): string {
  if (err && typeof err === "object") {
    const e = err as { code?: string; message?: string };
    switch (e.code) {
      case "FILE_TOO_LARGE":
        return "File is too large. Reduce size and try again.";
      case "UNSUPPORTED_MEDIA_TYPE":
        return "File format not supported. Use JPG, PNG, WebP, or GIF.";
      case "INVALID_IMAGE":
        return "Could not read the image. The file may be corrupted.";
      case "R2_NOT_CONFIGURED":
        return "Storage is not configured. Contact an administrator.";
      case "ADMIN_REQUIRED":
        return "Admin session required. Please sign in again.";
      case "AUTH_REQUIRED":
        return "Your session expired. Please sign in again.";
      default:
        return e.message || fallback;
    }
  }
  return fallback;
}

export function ImageUploader({
  value,
  onChange,
  label = "Image",
  maxSizeMB = 10,
  accept = "image/jpeg,image/png,image/webp,image/gif",
  disabled = false,
}: ImageUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [pickedFile, setPickedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Track object URLs so we can revoke them on unmount or replace.
  useEffect(() => {
    if (!previewUrl) return;
    return () => URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > maxSizeMB * 1024 * 1024) {
      setErrorMsg(`File exceeds ${maxSizeMB} MB limit`);
      setStatus("error");
      return;
    }
    setPickedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setStatus("selected");
    setErrorMsg(null);
  };

  const upload = async () => {
    if (!pickedFile) return;
    setStatus("uploading");
    setErrorMsg(null);
    try {
      const res = await adminApi.uploadImage(pickedFile);
      onChange(res.url);
      // Reset local state — parent now owns the URL.
      setPickedFile(null);
      setPreviewUrl(null);
      setStatus("idle");
    } catch (err) {
      setErrorMsg(formatErrorMessage(err));
      setStatus("error");
    }
  };

  const remove = () => {
    onChange("");
    setPickedFile(null);
    setPreviewUrl(null);
    setStatus("idle");
    setErrorMsg(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const displayUrl = value || previewUrl || "";

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={onPick}
        disabled={disabled || status === "uploading"}
        className="hidden"
      />

      {displayUrl ? (
        <div className="relative">
          <img
            src={displayUrl}
            alt=""
            className="h-32 w-full rounded-lg border border-zinc-200 object-cover dark:border-zinc-700"
          />
          {status === "uploading" && (
            <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/40">
              <Loader2 className="h-6 w-6 animate-spin text-white" />
            </div>
          )}
          {!disabled && (
            <button
              type="button"
              onClick={remove}
              className="absolute right-2 top-2 rounded-full bg-black/60 p-1.5 text-white hover:bg-black/80"
              aria-label="Remove image"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ) : (
        <div className="flex h-32 w-full items-center justify-center rounded-lg border border-dashed border-zinc-300 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/50">
          <ImageIcon className="h-8 w-8 text-zinc-300 dark:text-zinc-600" />
        </div>
      )}

      <div className="flex items-center gap-2">
        {!value && status !== "uploading" && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
          >
            <Upload className="mr-2 h-3.5 w-3.5" />
            {previewUrl ? "Choose different" : `Upload ${label.toLowerCase()}`}
          </Button>
        )}
        {value && !disabled && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
          >
            Replace
          </Button>
        )}
        {status === "selected" && pickedFile && (
          <Button
            type="button"
            size="sm"
            onClick={upload}
            disabled={disabled}
          >
            <Upload className="mr-2 h-3.5 w-3.5" />
            Upload &amp; save
          </Button>
        )}
        {pickedFile && status !== "uploading" && (
          <span className="truncate text-xs text-zinc-500 dark:text-zinc-400">
            {pickedFile.name} · {(pickedFile.size / 1024).toFixed(0)} KB
          </span>
        )}
      </div>

      {errorMsg && (
        <div className="rounded-lg bg-red-50 p-2 text-xs text-red-600 dark:bg-red-900/20 dark:text-red-400">
          {errorMsg}
        </div>
      )}
    </div>
  );
}